/**
 * Request/response shapes for the platform-operations surface (`BACKLOG.md` M4): practice
 * list/suspend/reactivate/tier-change, platform-operator management, and the platform's own
 * operational audit log. Validated on both sides against these schemas, same reasoning every other
 * `*Requests.ts` file in this package states — the server and the web app must not be able to
 * validate differently.
 *
 * Deliberately narrow to the *operational* slice this module's own header note in `BACKLOG.md`
 * argues for: nothing here reaches a chart, a patient, or any clinical content. A practice is
 * represented only by the columns `server/src/platform/db.ts`'s narrow role can actually read.
 */

import { z } from 'zod';

import { TIERS } from './entitlements.ts';

/**
 * One practice, as the platform list/suspend/reactivate/tier-change surface sees it — never the
 * full `orgs` row. `tier` is `z.enum(TIERS)` rather than `z.string()`: unlike `orgMembers.role` and
 * `orgs.tier`'s own storage (validated at the boundary rather than by a Postgres enum, per
 * `db/schema/platform.ts`'s own comment), a *response* schema can and should be exact, since the
 * server is the one producing the value and a widening here would just be a self-inflicted hole in
 * validation coverage.
 */
export const practiceSummarySchema = z.strictObject({
  id: z.uuid(),
  name: z.string(),
  tier: z.enum(TIERS),
  /**
   * Null means active. `db/schema/platform.ts`'s `orgs.suspendedAt` — see that column's own
   * comment for why this is a timestamp rather than a status enum.
   *
   * `z.string()` rather than `z.iso.datetime()`, matching every DB-echoed timestamp in
   * `clinicalRequests.ts` (`legalDocuments.ts`'s own comment on its `acceptedAt` states the same
   * reasoning): this value is read back from Postgres, not supplied by a caller, so it must not risk
   * a runtime throw against a strict ISO-format check a client-supplied instant would need instead.
   */
  suspendedAt: z.string().nullable(),
  /**
   * From `org_members` — active memberships only. Counted, never listed: the narrow platform-ops
   * role that produces this (`server/src/db/migrations/0013_platform_operators_and_org_suspension.
   * sql`) is granted `org_id`/`deleted_at` alone on that table, specifically so it can answer "how
   * many currently" and never
   * "who" — see that migration's own comment.
   */
  memberCount: z.number().int().nonnegative(),
});
export type PracticeSummary = z.infer<typeof practiceSummarySchema>;

export const listPracticesResponseSchema = z.strictObject({
  practices: z.array(practiceSummarySchema),
});
export type ListPracticesResponse = z.infer<typeof listPracticesResponseSchema>;

export const practiceResponseSchema = z.strictObject({
  practice: practiceSummarySchema,
});
export type PracticeResponse = z.infer<typeof practiceResponseSchema>;

/** `POST /api/platform/practices/:orgId/tier`. */
export const updatePracticeTierRequestSchema = z.strictObject({
  tier: z.enum(TIERS),
});
export type UpdatePracticeTierRequest = z.infer<typeof updatePracticeTierRequestSchema>;

/** One active platform-operator grant, joined to the granted user's email for readability — the
 * join happens through `users` (identity-scoped, unpolicied, same as `platform_operators` itself),
 * never through the narrow cross-tenant role, which holds no grant on `users` at all. */
export const platformOperatorSummarySchema = z.strictObject({
  userId: z.uuid(),
  email: z.string(),
  grantedByUserId: z.uuid(),
  /** DB-echoed — see `practiceSummarySchema.suspendedAt`'s comment on why this is `z.string()`. */
  grantedAt: z.string(),
});
export type PlatformOperatorSummary = z.infer<typeof platformOperatorSummarySchema>;

export const listPlatformOperatorsResponseSchema = z.strictObject({
  operators: z.array(platformOperatorSummarySchema),
});
export type ListPlatformOperatorsResponse = z.infer<typeof listPlatformOperatorsResponseSchema>;

/** Every way `POST /api/platform/operators/:userId/revoke` can refuse. */
export const PLATFORM_OPERATOR_ERROR_CODES = [
  /** No active grant exists for this user — nothing to revoke. */
  'not_a_platform_operator',
  /**
   * The zero-superuser guard: refusing to revoke the last remaining operator, which would leave
   * nobody able to grant the next one back in (only an existing operator's own session, or the
   * out-of-band bootstrap script, can ever create one). A trivial count check, load-bearing in what
   * it prevents.
   */
  'last_platform_operator',
] as const;
export type PlatformOperatorErrorCode = (typeof PLATFORM_OPERATOR_ERROR_CODES)[number];

/** `GET /api/platform/audit-log` — the platform's own operational events, never a practice's PHI-
 * read events (`shared/src/authorization.ts`'s own comment on `platform:read_audit_log`). */
export const platformAuditLogEntrySchema = z.strictObject({
  /** DB-echoed — see `practiceSummarySchema.suspendedAt`'s comment on why this is `z.string()`. */
  occurredAt: z.string(),
  actorUserId: z.uuid(),
  action: z.string(),
  resourceType: z.string(),
  resourceId: z.uuid().nullable(),
  /**
   * The `audit_events` shard this event is chained into. `audit_events.org_id` is `NOT NULL`
   * (`db/schema/audit.ts`), so every event — including a platform-operator grant or revoke, which
   * concerns no single practice — carries one: for a practice mutation it is the affected practice;
   * for an operator grant/revoke it is whichever practice (if any) the *acting* operator's own
   * session happened to be scoped to at the time, recorded honestly as "where this was chained," not
   * as a claim that the event is about that practice.
   */
  orgId: z.uuid(),
  outcome: z.enum(['allowed', 'denied']),
});
export type PlatformAuditLogEntry = z.infer<typeof platformAuditLogEntrySchema>;

export const platformAuditLogResponseSchema = z.strictObject({
  events: z.array(platformAuditLogEntrySchema),
});
export type PlatformAuditLogResponse = z.infer<typeof platformAuditLogResponseSchema>;
